import type { AIAnalysisResult } from '@/types';
import { PhotoComparisonResult } from '@/types';
import { authFetch } from '@/utils/authFetch';
import { compressImageForAI } from '@/utils/imageCompression';

const ENABLED = import.meta.env.VITE_ENABLE_AI_ANALYSIS === 'true';

/** Strips markdown fences and pulls out the {...} object even if the model added narration around it */
function extractJSON(text: string): any {
  const cleaned = text.replace(/```json\n?|```/g, '').trim();
  try {
    return JSON.parse(cleaned);
  } catch {
    const start = cleaned.indexOf('{');
    const end = cleaned.lastIndexOf('}');
    if (start !== -1 && end > start) {
      return JSON.parse(cleaned.slice(start, end + 1));
    }
    throw new Error('No JSON object found in response');
  }
}

/** Convert a Blob/File to base64 data */
async function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Compress and convert a Blob to base64, ready for Anthropic API */
async function prepareImageForAI(blob: Blob): Promise<{ base64Data: string; mediaType: string }> {
  console.log('[AI] Original blob size:', blob.size, 'bytes', `(${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
  const compressed = await compressImageForAI(blob);
  console.log('[AI] Compressed size:', compressed.size, 'bytes', `(${(compressed.size / 1024 / 1024).toFixed(2)} MB)`);
  const base64Data = await toBase64(compressed);
  console.log('[AI] Base64 length:', base64Data.length, 'chars');
  const mediaType = 'image/jpeg';
  return { base64Data, mediaType };
}

/**
 * Returns a FAIL result when AI analysis cannot be completed.
 * Failing is safer than passing — forces manual review.
 */
function analysisFailResult(reason: string): AIAnalysisResult {
  return {
    hasDamage: true,
    severity: 'moderate',
    description: `AI analysis unavailable — manual review required. Reason: ${reason}`,
    confidence: 0,
    detectedIssues: [`AI ANALYSIS FAILED: ${reason}. This check has been marked for manual review.`],
  };
}

/**
 * Retries a fetch call up to maxRetries times on transient errors (429, 529, 503).
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries = 2
): Promise<Response> {
  let lastErr: any;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const res = await authFetch(url, options);
      // Retry on rate limit or server overload
      if ((res.status === 429 || res.status === 529 || res.status === 503) && attempt < maxRetries) {
        const retryAfter = parseInt(res.headers.get('retry-after') ?? '3') * 1000;
        const delay = Math.max(retryAfter, 2000 * (attempt + 1));
        console.warn(`[AI] Received ${res.status} — retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(r => setTimeout(r, delay));
        continue;
      }
      return res;
    } catch (err) {
      lastErr = err;
      if (attempt < maxRetries) {
        const delay = 2000 * (attempt + 1);
        console.warn(`[AI] Fetch error — retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries}):`, err);
        await new Promise(r => setTimeout(r, delay));
      }
    }
  }
  throw lastErr;
}

/** Analyze an inspection photo for damage using Claude Vision */
export async function analyzePhoto(
  imageBlob: Blob,
  promptId?: string,
  promptLabel?: string,
): Promise<AIAnalysisResult> {
  console.log('[AI] analyzePhoto called — ENABLED:', ENABLED, '| promptId:', promptId);

  if (!ENABLED) {
    console.log('[AI] AI analysis disabled — returning mock pass result');
    return {
      hasDamage: false,
      severity: 'none',
      description: 'AI analysis is not configured. Photo saved without analysis.',
      confidence: 0,
      detectedIssues: [],
    };
  }

  try {
    const { base64Data, mediaType } = await prepareImageForAI(imageBlob);

    console.log('[AI] Sending to /api/analyze-photo...');
    const response = await fetchWithRetry('/api/analyze-photo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64Data, mediaType, promptId, promptLabel }),
    });

    console.log('[AI] /api/analyze-photo response status:', response.status);

    if (!response.ok) {
      const errText = await response.text();
      console.error('[AI] analyze-photo API error:', response.status, errText);
      return analysisFailResult(`API returned ${response.status}: ${errText.slice(0, 100)}`);
    }

    const data = await response.json();
    console.log('[AI] Raw Anthropic response:', JSON.stringify(data).slice(0, 300));
    const text = data.content?.[0]?.text || '';

    if (!text) {
      console.error('[AI] Empty response from Anthropic');
      return analysisFailResult('Empty response from AI service');
    }

    try {
      const parsed = extractJSON(text) as AIAnalysisResult;
      console.log('[AI] Parsed analysis result:', parsed);
      return parsed;
    } catch (parseErr) {
      console.error('[AI] Failed to parse AI response text:', text);
      return analysisFailResult('Could not parse AI response');
    }
  } catch (err: any) {
    console.error('[AI] analyzePhoto threw an exception:', err?.message ?? err);
    return analysisFailResult(err?.message ?? 'Unknown error');
  }
}

export interface VehicleValidationResult {
  isSameVehicle: boolean;
  confidence: number;
  reasoning: string;
  vehicleDescription: string;
}

export async function validateVehicleConsistency(
  imageBlob: Blob,
  referenceDescription?: string,
  expectedVehicle?: string,
): Promise<VehicleValidationResult> {
  console.log('[AI] validateVehicleConsistency called | expectedVehicle:', expectedVehicle);

  try {
    const { base64Data, mediaType } = await prepareImageForAI(imageBlob);

    console.log('[AI] Sending to /api/analyze-photo/validate-vehicle...');
    const response = await fetchWithRetry('/api/analyze-photo/validate-vehicle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ base64Data, mediaType, referenceDescription, expectedVehicle }),
    });

    console.log('[AI] validate-vehicle response status:', response.status);

    if (!response.ok) {
      const errText = await response.text();
      console.error('[AI] validate-vehicle API error:', response.status, errText);
      // On validation failure, assume same vehicle to avoid blocking the user
      return {
        isSameVehicle: true,
        confidence: 0,
        reasoning: `Validation unavailable (${response.status}) — proceeding without check`,
        vehicleDescription: referenceDescription ?? '',
      };
    }

    const result = await response.json();
    console.log('[AI] Validation result:', result);
    return result;
  } catch (err: any) {
    console.error('[AI] validateVehicleConsistency threw an exception:', err?.message ?? err);
    return {
      isSameVehicle: true,
      confidence: 0,
      reasoning: 'Validation unavailable — proceeding without check',
      vehicleDescription: referenceDescription ?? '',
    };
  }
}

export async function compareWithPreviousPhoto(
  currentFile: File,
  previousUri: string,
  promptLabel: string,
  inspectionType: string,
): Promise<PhotoComparisonResult> {
  console.log('[AI] compareWithPreviousPhoto called | promptLabel:', promptLabel);

  try {
    const compressedCurrent = await compressImageForAI(currentFile);
    const currentBase64 = await toBase64(compressedCurrent);

    const prevResponse = await fetch(previousUri);
    const prevBlob = await prevResponse.blob();
    const compressedPrev = await compressImageForAI(prevBlob);
    const previousBase64 = await toBase64(compressedPrev);

    const mediaType = 'image/jpeg';

    console.log('[AI] Sending to /api/analyze-photo/compare...');
    const response = await fetchWithRetry('/api/analyze-photo/compare', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        currentBase64,
        previousBase64,
        mediaType,
        promptLabel,
        inspectionType,
      }),
    });

    console.log('[AI] compare response status:', response.status);

    if (!response.ok) {
      const errText = await response.text();
      console.error('[AI] compare API error:', response.status, errText);
      return {
        hasChanges: false,
        severity: 'none',
        changes: [],
        summary: 'Comparison unavailable — could not contact AI service',
      };
    }

    return response.json();
  } catch (err: any) {
    console.error('[AI] compareWithPreviousPhoto threw an exception:', err?.message ?? err);
    return {
      hasChanges: false,
      severity: 'none',
      changes: [],
      summary: 'Comparison unavailable',
    };
  }
}